import React, { useEffect, useState } from 'react';
import { Button, Table, Space, Modal, Form, Input, message } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { supabase } from '../../lib/supabase';
import { Reward } from '../../types/missions';
import { useNavigate } from 'react-router-dom';
import { SUPABASE_IMAGE_BASE_URL } from '../../lib/constants';
import { PageLayout } from '../../components/layout/PageLayout';

const AdminRewards: React.FC = () => {
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingReward, setEditingReward] = useState<Reward | null>(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  useEffect(() => {
    loadRewards();
  }, []);

  const loadRewards = async () => {
    try {
      const { data, error } = await supabase
        .from('rewards')
        .select('*')
        .order('name');

      if (error) throw error;

      setRewards(data.map(reward => ({
        id: reward.id,
        name: reward.name,
        image_url: reward.image_url
      })));
    } catch (error) {
      message.error('Erreur lors du chargement des récompenses');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    form.resetFields();
    setEditingReward(null);
    setIsModalVisible(true);
  };

  const handleEdit = (reward: Reward) => {
    form.setFieldsValue(reward);
    setEditingReward(reward);
    setIsModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('rewards')
        .delete()
        .eq('id', id);

      if (error) throw error;

      message.success('Récompense supprimée avec succès');
      loadRewards();
    } catch (error) {
      message.error('Erreur lors de la suppression de la récompense');
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const imageUrl = values.image_url.startsWith('rewards/') 
        ? values.image_url 
        : `rewards/${values.image_url}`;
        
      const rewardData = {
        name: values.name,
        image_url: imageUrl
      };

      if (editingReward) {
        const { error } = await supabase
          .from('rewards')
          .update(rewardData)
          .eq('id', editingReward.id);

        if (error) throw error;
        message.success('Récompense mise à jour avec succès');
      } else {
        const { error } = await supabase
          .from('rewards')
          .insert([rewardData]);

        if (error) throw error;
        message.success('Récompense créée avec succès');
      }

      setIsModalVisible(false);
      loadRewards();
    } catch (error) {
      message.error('Erreur lors de la sauvegarde de la récompense');
    }
  };

  const columns = [
    {
      title: 'Image',
      dataIndex: 'image_url',
      key: 'image_url',
      render: (image_url: string) => (
        <img 
          src={`${SUPABASE_IMAGE_BASE_URL}${image_url}`} 
          alt="Récompense" 
          className="w-12 h-12 object-contain"
        />
      ),
    },
    {
      title: 'Nom',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Reward) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Modifier
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            Supprimer
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <PageLayout disableAds={true}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin')}
              icon={<ArrowLeftOutlined />}
            >
              Retour
            </Button>
            <h1 className="text-2xl font-bold">Gestion des Récompenses</h1>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            Nouvelle Récompense
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={rewards}
          loading={loading}
          rowKey="id"
        />

        <Modal
          title={editingReward ? 'Modifier la Récompense' : 'Nouvelle Récompense'}
          open={isModalVisible}
          onOk={handleModalOk}
          onCancel={() => setIsModalVisible(false)}
        >
          <Form
            form={form}
            layout="vertical"
          >
            <Form.Item
              name="name"
              label="Nom"
              rules={[{ required: true, message: 'Le nom est requis' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="image_url"
              label="URL de l'image"
              rules={[{ required: true, message: "L'URL de l'image est requise" }]}
            >
              <Input />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </PageLayout>
  );
};

export default AdminRewards; 